/**
 * Supabase Realtime Sync Hook
 *
 * Provides real-time synchronization for multi-user scenarios.
 * When one user makes changes, all other users see updates instantly.
 *
 * Usage:
 *   useRealtimeSync('deals');  // Subscribe to deals table changes
 *   useRealtimeSync(['deals', 'activities']);  // Multiple tables
 */
import { useEffect, useRef } from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { queryKeys, DEALS_VIEW_KEY, DEALS_BY_STAGE_KEY } from '@/lib/query/queryKeys';
import { applyDealPatchToCaches } from '@/lib/query/hooks/useDealsQuery';
import { adjustStageCount } from '@/lib/query/hooks/useDealCountByStage';
import { useAuth } from '@/context/AuthContext';
import type { DealView } from '@/types';
import type { MessagingMessage } from '@/lib/messaging';
import { transformMessage } from '@/lib/messaging/types';
import type { DbMessagingMessage, ConversationView } from '@/lib/messaging/types';
import { pendingDeletionIds, removePendingDeletion, pendingAcknowledgedIds } from '@/lib/query/hooks/useConversationsQuery';

// Enable detailed Realtime logging in development or when DEBUG_REALTIME env var is set
const DEBUG_REALTIME = process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_DEBUG_REALTIME === 'true';

// Global deduplication for INSERT events - prevents multiple hook instances from processing the same event
// Key format: `${table}-${id}-${updatedAt}`
// Using Map with timestamp to handle TTL and atomic check-and-set
const processedInserts = new Map<string, number>();
const PROCESSED_CACHE_TTL = 5000; // 5 seconds TTL for processed events

// Atomic check-and-set for deduplication
function shouldProcessInsert(key: string): boolean {
  const now = Date.now();
  
  // Clean up old entries
  for (const [k, timestamp] of processedInserts) {
    if (now - timestamp > PROCESSED_CACHE_TTL) {
      processedInserts.delete(k);
    }
  }
  
  // Check if already processed
  if (processedInserts.has(key)) {
    return false;
  }
  
  // Mark as processed immediately (atomic in single-threaded JS)
  processedInserts.set(key, now);
  return true;
}

// Tables that support realtime sync
type RealtimeTable =
  | 'deals'
  | 'contacts'
  | 'activities'
  | 'boards'
  | 'board_stages'
  | 'crm_companies'
  // Messaging tables
  | 'messaging_channels'
  | 'messaging_conversations'
  | 'messaging_messages';

// Lazy getter for query keys mapping - avoids initialization issues in tests
const getTableQueryKeys = (table: RealtimeTable): readonly (readonly unknown[])[] => {
  const mapping: Record<RealtimeTable, readonly (readonly unknown[])[]> = {
    deals: [queryKeys.deals.all, queryKeys.dashboard.stats],
    contacts: [queryKeys.contacts.all],
    activities: [queryKeys.activities.all],
    boards: [queryKeys.boards.all],
    board_stages: [queryKeys.boards.all], // stages invalidate boards
    crm_companies: [queryKeys.companies.all],
    // Messaging tables
    messaging_channels: [queryKeys.messagingChannels.all],
    messaging_conversations: [
      queryKeys.messagingConversations.all,
      queryKeys.messagingConversations.unreadCount(),
    ],
    // messaging_messages uses targeted invalidation via conversation_id
    // (see handleMessagingMessageChange below). This fallback covers edge cases
    // where payload doesn't contain conversation_id.
    messaging_messages: [queryKeys.messagingMessages.all],
  };
  return mapping[table];
};

interface UseRealtimeSyncOptions {
  /** Whether sync is enabled (default: true) */
  enabled?: boolean;
  /** Debounce invalidation to avoid rapid updates (ms) */
  debounceMs?: number;
  /** Callback when a change is received */
  onchange?: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void;
}

/**
 * Subscribe to realtime changes on one or more tables
 */
export function useRealtimeSync(
  tables: RealtimeTable | RealtimeTable[],
  options: UseRealtimeSyncOptions = {}
) {
  const { enabled = true, debounceMs = 100, onchange } = options;
  const queryClient = useQueryClient();
  // Filtra eventos por organization_id no servidor (em vez de ignorar
  // server-side via RLS depois). Reduz drasticamente o tráfego no websocket
  // em multi-tenant — sem isso, todo INSERT/UPDATE de qualquer org chega no
  // cliente e é descartado pela RLS do Postgres.
  const { organizationId } = useAuth();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const isConnectedRef = useRef(false);
  const instanceIdRef = useRef(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingInvalidationsRef = useRef<Set<readonly unknown[]>>(new Set());
  const pendingInvalidateOnlyRef = useRef<Set<readonly unknown[]>>(new Set());
  // Track bursty board_stages INSERTs (creating a board inserts multiple stages).
  // We'll refetch on single INSERT (realtime stage created by someone else),
  // but avoid storms on bursts (treat burst as invalidate-only).
  const pendingBoardStagesInsertCountRef = useRef(0);
  const flushScheduledRef = useRef(false);
  const onchangeRef = useRef(onchange);
  
  // Keep callback ref up to date without causing re-renders
  useEffect(() => {
    onchangeRef.current = onchange;
  }, [onchange]);

  useEffect(() => {
    if (!enabled) return;
    // Sem organizationId, não criamos canal: evita janela onde subscrição roda
    // sem filtro server-side (multi-tenant) e evita recriar o canal quando
    // organizationId transiciona de null → uuid durante a carga do auth.
    if (!organizationId) {
      if (DEBUG_REALTIME) {
        console.log('[Realtime] skipping channel creation — organizationId not ready');
      }
      return;
    }

    const sb = supabase;
    if (!sb) {
      console.warn('[Realtime] Supabase client not available');
      return;
    }

    const tableList = Array.isArray(tables) ? tables : [tables];
    // Use unique instance ID to avoid conflict with channel being disconnected
    instanceIdRef.current += 1;
    const channelName = `realtime-sync-${tableList.join('-')}-${organizationId}-${instanceIdRef.current}`;

    // Cleanup existing channel if any (detach-and-forget pattern)
    if (channelRef.current) {
      if (DEBUG_REALTIME) {
        console.log(`[Realtime] Cleaning up existing channel`);
      }
      const oldChannel = channelRef.current;
      channelRef.current = null;
      sb.removeChannel(oldChannel);
    }

    // Create channel with unique name to avoid race condition with previous removal
    const channel = sb.channel(channelName);

    // Subscribe to each table
    tableList.forEach(table => {
      // Server-side filter: só recebemos eventos da própria org. Se ainda não
      // temos orgId (auth carregando), pulamos a subscrição — o useEffect roda
      // de novo quando organizationId muda (incluído nas deps abaixo).
      const filterConfig: {
        event: '*';
        schema: string;
        table: RealtimeTable;
        filter?: string;
      } = {
        event: '*',
        schema: 'public',
        table,
      };
      if (organizationId) {
        filterConfig.filter = `organization_id=eq.${organizationId}`;
      }
      channel.on(
        'postgres_changes',
        filterConfig,
        (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
          if (DEBUG_REALTIME) {
            console.log(`[Realtime] ${table} ${payload.eventType}:`, payload);
          }

          // #region agent log
          if (process.env.NODE_ENV !== 'production') {
            const dealId = (payload.new as Record<string, unknown>)?.id || (payload.old as Record<string, unknown>)?.id;
            const newData = payload.new as Record<string, unknown>;
            const oldData = payload.old as Record<string, unknown>;
            const logData = {
              dealId: typeof dealId === 'string' ? dealId.slice(0, 8) : '',
              eventType: payload.eventType,
              newStatus: newData?.status ? String(newData.status).slice(0, 8) : '',
              oldStatus: oldData?.status ? String(oldData.status).slice(0, 8) : '',
              newStageId: newData?.stage_id ? String(newData.stage_id).slice(0, 8) : '',
              oldStageId: oldData?.stage_id ? String(oldData.stage_id).slice(0, 8) : '',
              newUpdatedAt: newData?.updated_at || newData?.updatedAt || '',
              oldUpdatedAt: oldData?.updated_at || oldData?.updatedAt || '',
            };
            console.log(`[Realtime] 📨 Event received: ${table} ${payload.eventType}`, logData);
          }
          // #endregion

          // Call custom callback (if provided)
          onchangeRef.current?.(payload);

          // Targeted handling for messaging_messages:
          // - UPDATE: patch the message status in-cache (no refetch)
          // - INSERT/DELETE: invalidate to sync new/removed messages
          if (table === 'messaging_messages') {
            const record = (payload.new || payload.old) as Record<string, unknown>;
            const conversationId = record?.conversation_id as string | undefined;
            if (conversationId) {
              if (payload.eventType === 'UPDATE' && payload.new) {
                const db = payload.new as Record<string, unknown>;
                const messageId = db.id as string;
                const patch: Partial<MessagingMessage> = {
                  status: db.status as MessagingMessage['status'],
                  ...(db.external_id != null && { externalId: db.external_id as string }),
                  ...(db.sent_at != null && { sentAt: db.sent_at as string }),
                  ...(db.delivered_at != null && { deliveredAt: db.delivered_at as string }),
                  ...(db.read_at != null && { readAt: db.read_at as string }),
                  ...(db.failed_at != null && { failedAt: db.failed_at as string }),
                  ...(db.error_code != null && { errorCode: db.error_code as string }),
                  ...(db.error_message != null && { errorMessage: db.error_message as string }),
                  // Include metadata so reaction updates (metadata.reactions) propagate to the UI
                  ...(db.metadata != null && { metadata: db.metadata as Record<string, unknown> }),
                  // Include content so late-arriving mediaUrl/transcription (audio archiving,
                  // image/video processing) propagate to the UI without a manual refresh
                  ...(db.content != null && { content: db.content as MessagingMessage['content'] }),
                };

                const applyPatch = (m: MessagingMessage) =>
                  m.id === messageId ? { ...m, ...patch } : m;

                // Update flat query cache
                const flatKey = queryKeys.messagingMessages.byConversation(conversationId);
                queryClient.setQueryData<MessagingMessage[]>(flatKey, (old) =>
                  old ? old.map(applyPatch) : old
                );

                // Update infinite query cache (used by MessageThread)
                const infiniteKey = [...flatKey, 'infinite'] as const;
                queryClient.setQueryData<InfiniteData<{ messages: MessagingMessage[]; nextCursor: string | null }>>(
                  infiniteKey,
                  (old) => old
                    ? { ...old, pages: old.pages.map(p => ({ ...p, messages: p.messages.map(applyPatch) })) }
                    : old
                );
                return; // No invalidation for UPDATE
              }

              // INSERT: inject inbound messages directly into cache for instant delivery.
              // Invalidate-then-refetch relies on RLS evaluation in Supabase Realtime,
              // which can fail for complex JOIN-based policies — causing messages to not appear
              // until the user manually refreshes. Direct cache injection bypasses that entirely.
              if (payload.eventType === 'INSERT') {
                const direction = (payload.new as Record<string, unknown>)?.direction;
                if (direction === 'outbound') {
                  // Our own outbound INSERT — useSendMessage already placed the message in cache.
                  // Only refresh the conversations list (last_message preview).
                  pendingInvalidationsRef.current.add(queryKeys.messagingConversations.all);
                  pendingInvalidationsRef.current.add(queryKeys.messagingConversations.unreadCount());
                  if (!flushScheduledRef.current) {
                    flushScheduledRef.current = true;
                    queueMicrotask(() => {
                      flushScheduledRef.current = false;
                      const keysToFlush = Array.from(pendingInvalidationsRef.current);
                      pendingInvalidationsRef.current.clear();
                      keysToFlush.forEach((queryKey) => {
                        // Skip conversations-list invalidation while a deletion is in-progress.
                        // The shared ref may have been contaminated by a concurrent UPDATE event
                        // (e.g. markAsRead). Flushing it here causes a refetch that returns the
                        // conversation from DB before it's deleted, creating a flicker.
                        if (pendingDeletionIds.size > 0 && queryKey === queryKeys.messagingConversations.all) {
                          return;
                        }
                        queryClient.invalidateQueries({ queryKey, exact: false, refetchType: 'active' });
                      });
                    });
                  }
                  return;
                }

                // Inbound message: inject directly into the infinite query cache.
                // This is instant and doesn't require a network roundtrip or RLS re-evaluation.
                const newMessage = transformMessage(payload.new as unknown as DbMessagingMessage);
                const flatKey = queryKeys.messagingMessages.byConversation(conversationId);
                const infiniteKey = [...flatKey, 'infinite'] as const;

                queryClient.setQueryData<InfiniteData<{ messages: MessagingMessage[]; nextCursor: string | null }>>(
                  infiniteKey,
                  (old) => {
                    if (!old) return old;
                    // Append to the last page (most recent messages page).
                    // MessageThread reverses the order, so appending here is correct.
                    const pages = old.pages.map((page, i) => {
                      if (i !== old.pages.length - 1) return page;
                      // Deduplicate: skip if message already in cache
                      if (page.messages.some((m) => m.id === newMessage.id)) return page;
                      return { ...page, messages: [...page.messages, newMessage] };
                    });
                    return { ...old, pages };
                  }
                );

                if (DEBUG_REALTIME) {
                  console.log('[Realtime] 💬 Inbound message injected into cache:', newMessage.id);
                }

                // Also refresh conversations list for last_message preview + unread count.
                pendingInvalidationsRef.current.add(queryKeys.messagingConversations.all);
                pendingInvalidationsRef.current.add(queryKeys.messagingConversations.unreadCount());
                if (!flushScheduledRef.current) {
                  flushScheduledRef.current = true;
                  queueMicrotask(() => {
                    flushScheduledRef.current = false;
                    const keysToFlush = Array.from(pendingInvalidationsRef.current);
                    pendingInvalidationsRef.current.clear();
                    keysToFlush.forEach((queryKey) => {
                      // Skip conversations-list invalidation while a deletion is in-progress.
                      // Same guard as the outbound path — prevents an inbound message arriving
                      // during the delete window from triggering a refetch that re-shows the
                      // deleted conversation before the realtime DELETE event lowers the guard.
                      if (pendingDeletionIds.size > 0 && queryKey === queryKeys.messagingConversations.all) {
                        return;
                      }
                      queryClient.invalidateQueries({ queryKey, exact: false, refetchType: 'active' });
                    });
                  });
                }
                return;
              }

              // DELETE: invalidate to sync message thread only.
              // Do NOT invalidate messagingConversations here — that causes a race condition
              // when deleting a conversation: messages are deleted first, firing this event
              // before the conversation is deleted. The queueMicrotask refetch (refetchType:'all')
              // runs while the conversation still exists in DB, causing it to flash back into
              // the list. The messaging_conversations DELETE event handles list invalidation.
              const flatKey = queryKeys.messagingMessages.byConversation(conversationId);
              const infiniteKey = [...flatKey, 'infinite'] as const;
              pendingInvalidationsRef.current.add(flatKey);
              pendingInvalidationsRef.current.add(infiniteKey);

              if (!flushScheduledRef.current) {
                flushScheduledRef.current = true;
                queueMicrotask(() => {
                  flushScheduledRef.current = false;
                  const keysToFlush = Array.from(pendingInvalidationsRef.current);
                  pendingInvalidationsRef.current.clear();
                  keysToFlush.forEach((queryKey) => {
                    // Skip conversations-list invalidation while a deletion is in-progress.
                    // The shared ref may be contaminated by a concurrent UPDATE event (e.g.
                    // markAsRead). Flushing messagingConversations.all here with refetchType:'all'
                    // would return the conversation from DB before it's deleted — causing a flicker.
                    if (pendingDeletionIds.size > 0 && queryKey === queryKeys.messagingConversations.all) {
                      return;
                    }
                    queryClient.invalidateQueries({ queryKey, exact: false, refetchType: 'active' });
                  });
                });
              }
              return;
            }
            // If no conversation_id, fall through to generic invalidation
          }

          // Targeted handling for messaging_conversations DELETE:
          // Instead of calling invalidateQueries (which triggers a refetch), remove the
          // conversation from the cache directly. This prevents the flicker caused by
          // the refetch returning stale data (conversation still in DB while messages
          // are being deleted) overwriting the optimistic removal.
          if (table === 'messaging_conversations' && payload.eventType === 'DELETE') {
            const deletedId = (payload.old as Record<string, unknown>)?.id as string | undefined;
            if (deletedId) {
              // The DELETE event is the authoritative confirmation that the conversation is gone.
              // Lower the guard here so subsequent invalidations (e.g. unreadCount refetch) are
              // not blocked. This is the correct place — earlier removal (e.g. in onSettled) races
              // with DB-trigger UPDATE events that arrive via WebSocket AFTER the HTTP response.
              removePendingDeletion(deletedId);
              queryClient.setQueriesData(
                { queryKey: queryKeys.messagingConversations.all },
                (old: unknown) => {
                  if (!Array.isArray(old)) return old;
                  return (old as ConversationView[]).filter((conv) => conv.id !== deletedId);
                }
              );
              queryClient.removeQueries({
                queryKey: queryKeys.messagingConversations.detail(deletedId),
              });
              queryClient.invalidateQueries({
                queryKey: queryKeys.messagingConversations.unreadCount(),
              });
              if (DEBUG_REALTIME) {
                console.log('[Realtime] 🗑️ messaging_conversations DELETE — removed from cache directly', deletedId);
              }
            }
            return; // Skip generic invalidation path
          }

          // Skip UPDATE/INSERT events for conversations currently being deleted.
          // When messages are deleted, the DB trigger fires an UPDATE on messaging_conversations
          // (updating last_message_at, message_count, etc.) that arrives via WebSocket AFTER the
          // HTTP DELETE response. If we let it through, it queues a conversations-list refetch
          // that returns the conversation (still in DB at that instant), causing the flicker.
          if (table === 'messaging_conversations' && payload.eventType !== 'DELETE') {
            const convId = ((payload.new || payload.old) as Record<string, unknown>)?.id as string | undefined;
            if (convId && pendingDeletionIds.has(convId)) {
              if (DEBUG_REALTIME) {
                console.log('[Realtime] ⏭️ Skip conversations UPDATE for pending deletion:', convId.slice(0, 8));
              }
              return;
            }
            // Skip realtime refetch while an acknowledge (dismiss AGUARDANDO) is in-flight.
            // The optimistic update already hides the conversation; allowing a refetch before
            // the POST commits would return it as still-pending and cause the flicker.
            if (convId && pendingAcknowledgedIds.has(convId)) {
              if (DEBUG_REALTIME) {
                console.log('[Realtime] ⏭️ Skip conversations UPDATE for pending acknowledge:', convId.slice(0, 8));
              }
              return;
            }
          }

          // Queue query keys for invalidation (lazy loaded)
          const keys = getTableQueryKeys(table);
          // NOTE: `board_stages` INSERTs happen in bursts when creating a board (one per stage).
          // Refetching boards on each stage INSERT causes a request storm.
          // For that specific case, we can refetch on a single INSERT (true realtime),
          // but treat bursts as invalidate-only and let the board create mutation handle timing.
          if (payload.eventType === 'INSERT' && table === 'board_stages') {
            keys.forEach(key => pendingInvalidateOnlyRef.current.add(key));
            pendingBoardStagesInsertCountRef.current += 1;
          } else {
            keys.forEach(key => pendingInvalidationsRef.current.add(key));
          }

          // INSERT events can happen in bursts (ex.: creating a board inserts multiple board_stages).
          // Instead of refetching per-row, batch within the same tick using a microtask.
          // This keeps UI instant (optimistic updates handle UX) while preventing refetch storms.
          if (payload.eventType === 'INSERT') {
            // SPECIAL HANDLING FOR DEALS INSERT:
            // Instead of invalidating (which causes refetch that removes temp deal),
            // add the deal directly to the cache. This prevents the "flash and disappear" bug.
            if (table === 'deals') {
              const newData = payload.new as Record<string, unknown>;
              const dealId = newData.id as string;
              const updatedAt = newData.updated_at as string;
              
              // Deduplication: prevent multiple hook instances from processing the same INSERT
              const dedupeKey = `deals-${dealId}-${updatedAt}`;
              if (!shouldProcessInsert(dedupeKey)) {
                // #region agent log
                if (process.env.NODE_ENV !== 'production') {
                  console.log(`[Realtime] ⏭️ INSERT deals - skipping duplicate`, { dealId: dealId.slice(0, 8) });
                }
                // #endregion
                return; // Skip this event, already processed by another hook instance
              }
              
              // #region agent log
              if (process.env.NODE_ENV !== 'production') {
                const logData = {
                  dealId: dealId.slice(0, 8),
                  title: newData.title || 'null',
                  status: typeof newData.stage_id === 'string' ? (newData.stage_id as string).slice(0, 8) : 'null',
                };
                console.log(`[Realtime] 📥 INSERT deals - adding to cache directly`, logData);
              }
              // #endregion

              // Normalize snake_case to camelCase for cache compatibility
              const normalizedDeal: Record<string, unknown> = { ...newData };
              if (newData.stage_id !== undefined) {
                normalizedDeal.status = newData.stage_id;
                delete normalizedDeal.stage_id;
              }
              if (newData.updated_at !== undefined) {
                normalizedDeal.updatedAt = newData.updated_at;
                delete normalizedDeal.updated_at;
              }
              if (newData.created_at !== undefined) {
                normalizedDeal.createdAt = newData.created_at;
                delete normalizedDeal.created_at;
              }
              if (newData.is_won !== undefined) {
                normalizedDeal.isWon = newData.is_won;
                delete normalizedDeal.is_won;
              }
              if (newData.is_lost !== undefined) {
                normalizedDeal.isLost = newData.is_lost;
                delete normalizedDeal.is_lost;
              }
              if (newData.board_id !== undefined) {
                normalizedDeal.boardId = newData.board_id;
                delete normalizedDeal.board_id;
              }
              if (newData.contact_id !== undefined) {
                normalizedDeal.contactId = newData.contact_id;
                delete normalizedDeal.contact_id;
              }
              if (newData.company_id !== undefined) {
                normalizedDeal.companyId = newData.company_id;
                delete normalizedDeal.company_id;
              }
              if (newData.closed_at !== undefined) {
                normalizedDeal.closedAt = newData.closed_at;
                delete normalizedDeal.closed_at;
              }
              if (newData.last_stage_change_date !== undefined) {
                normalizedDeal.lastStageChangeDate = newData.last_stage_change_date;
                delete normalizedDeal.last_stage_change_date;
              }
              if (newData.organization_id !== undefined) {
                normalizedDeal.organizationId = newData.organization_id;
                delete normalizedDeal.organization_id;
              }
              if (newData.loss_reason !== undefined) {
                normalizedDeal.lossReason = newData.loss_reason;
                delete normalizedDeal.loss_reason;
              }

              // Fase 1B: patcha TODOS os caches relevantes (full DEALS_VIEW_KEY +
              // todos os DEALS_BY_BOARD_KEY ativos). O patcher precisa filtrar
              // só deals do mesmo board ao adicionar (mas o cache full deixa
              // passar tudo). Como o helper aplica o mesmo patcher em todos,
              // o `boardId` do deal é garantido pelo próprio cache: se o cache
              // for de outro board, o deal ainda é adicionado mas client-side
              // o filtro garante coerência (queryFn filtra por board_id).
              //
              // Edge case importante: ao adicionar num cache de board DIFERENTE
              // do board do deal, o deal apareceria erradamente. Solução: o
              // patcher chega ao cache e checa se algum deal existente tem
              // boardId diferente; se o cache estiver "limpo" pra outro board,
              // não adicionamos.
              const dealBoardId = typeof newData.board_id === 'string' ? newData.board_id : null;
              applyDealPatchToCaches(queryClient, (old) => {
                if (!old || !Array.isArray(old)) return old;

                // Se o cache não tem deals do board do INSERT, é cache de outro
                // board — não adicionar lá. Identificamos por amostragem:
                // pegamos qualquer deal do cache e comparamos boardId.
                if (dealBoardId && old.length > 0) {
                  const cacheBoardSample = old[0]?.boardId;
                  // Se TODOS os deals do cache têm boardId !== dealBoardId,
                  // este é um cache de OUTRO board. Pular.
                  const allDifferentBoard = old.every(d => d.boardId && d.boardId !== dealBoardId);
                  if (allDifferentBoard && cacheBoardSample) {
                    return old;
                  }
                }

                // Check if deal already exists (by real ID)
                const existingIndex = old.findIndex((d) => d.id === dealId);
                if (existingIndex !== -1) {
                  if (process.env.NODE_ENV !== 'production') {
                    console.log(`[Realtime] 📥 INSERT deals - deal already exists, updating`, { dealId: dealId.slice(0, 8) });
                  }
                  return old.map((d, i) => i === existingIndex ? { ...d, ...normalizedDeal } as DealView : d);
                }

                // Remove any temp deals with same title (they are placeholders for this deal)
                const tempDealsRemoved = old.filter((d) => {
                  const isTemp = typeof d.id === 'string' && d.id.startsWith('temp-');
                  const sameTitle = d.title === newData.title;
                  return !(isTemp && sameTitle);
                });

                if (process.env.NODE_ENV !== 'production') {
                  const removedCount = old.length - tempDealsRemoved.length;
                  console.log(`[Realtime] 📥 INSERT deals - adding new deal to cache`, {
                    dealId: dealId.slice(0, 8),
                    removedTempDeals: removedCount,
                    cacheSize: tempDealsRemoved.length + 1,
                  });
                }

                return [normalizedDeal as unknown as DealView, ...tempDealsRemoved];
              });
              
              // Don't invalidate for deals INSERT - we've added it directly
              return;
            }
            
            if (!flushScheduledRef.current) {
              flushScheduledRef.current = true;
              queueMicrotask(() => {
                flushScheduledRef.current = false;

                const keysToFlush = Array.from(pendingInvalidationsRef.current);
                pendingInvalidationsRef.current.clear();
                const keysInvalidateOnly = Array.from(pendingInvalidateOnlyRef.current);
                pendingInvalidateOnlyRef.current.clear();
                const boardStagesInsertCount = pendingBoardStagesInsertCountRef.current;
                pendingBoardStagesInsertCountRef.current = 0;

                keysToFlush.forEach((queryKey) => {
                  queryClient.invalidateQueries({
                    queryKey,
                    exact: false,
                    refetchType: 'active',
                  });
                });

                // For bursty INSERT sources (ex.: board_stages create-board burst),
                // invalidate-only (no refetch) to avoid storms. But for single INSERT, refetch to keep realtime UX.
                keysInvalidateOnly.forEach((queryKey) => {
                  queryClient.invalidateQueries({
                    queryKey,
                    exact: false,
                    refetchType: boardStagesInsertCount <= 1 ? 'active' : 'none',
                  });
                });
              });
            }
          } else {
            // Handle deals DELETE: remove from cache directly.
            // Previously this fell through to invalidateQueries(['deals']), which triggered a full
            // refetch. The refetch occasionally zeroed the list (race condition with auth token,
            // cancellation by a concurrent mutation's cancelQueries, or RPC returning 0 rows).
            // Removing the deal surgically from DEALS_VIEW_KEY avoids any refetch entirely.
            if (payload.eventType === 'DELETE' && table === 'deals') {
              const deletedId = (payload.old as Record<string, unknown>)?.id as string | undefined;
              if (deletedId) {
                applyDealPatchToCaches(queryClient, (old) => {
                  if (!old || !Array.isArray(old)) return old;
                  return old.filter((d) => d.id !== deletedId);
                });
                queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats });
                if (DEBUG_REALTIME) {
                  console.log('[Realtime] 🗑️ deals DELETE — removed from cache directly', deletedId?.slice(0, 8));
                }
              }
              return;
            }

            // For deals UPDATE: apply directly to cache to avoid race condition with optimistic updates
            // When user moves a deal:
            // 1. Optimistic update moves it visually
            // 2. Server confirms
            // 3. Realtime UPDATE arrives
            // If we invalidate here, we might refetch stale data and the deal "jumps back"
            // Instead, apply the update directly to cache
            if (payload.eventType === 'UPDATE' && table === 'deals') {
              const newData = payload.new as Record<string, unknown>;
              const oldData = payload.old as Record<string, unknown>;
              const dealId = newData.id as string;
              // CRITICAL: Realtime sends stage_id as the source of truth for deal stage.
              // The `status` field in Realtime payload may be stale/incorrect.
              // Always prioritize stage_id over status!
              const incomingStatus = typeof newData.stage_id === 'string' ? newData.stage_id : 
                                    typeof newData.status === 'string' ? newData.status : null;
              const payloadOldStatus = typeof oldData.stage_id === 'string' ? oldData.stage_id :
                                       typeof oldData.status === 'string' ? oldData.status : null;
              
              // #region agent log
              if (process.env.NODE_ENV !== 'production') {
                const incomingUpdatedAtRaw = (newData.updated_at || newData.updatedAt) as string | undefined;
                const logData = {
                  dealId: dealId.slice(0, 8),
                  incomingStatus: incomingStatus?.slice(0, 8) || 'null',
                  payloadOldStatus: payloadOldStatus?.slice(0, 8) || 'null',
                  incomingUpdatedAt: incomingUpdatedAtRaw || 'null',
                  hasOldData: !!payloadOldStatus,
                  // Debug: show both status and stage_id to understand payload
                  rawStatus: typeof newData.status === 'string' ? (newData.status as string).slice(0, 8) : 'null',
                  rawStageId: typeof newData.stage_id === 'string' ? (newData.stage_id as string).slice(0, 8) : 'null',
                };
                console.log(`[Realtime] 🔍 Processing deals UPDATE`, logData);
              }
              // #endregion

              // Soft-delete: if deleted_at is being set, remove deal from cache immediately.
              // The deals_enriched view filters WHERE deleted_at IS NULL, so the deal will
              // disappear on next refetch anyway — remove it now to avoid a stale card.
              if (newData.deleted_at !== null && newData.deleted_at !== undefined) {
                applyDealPatchToCaches(queryClient, (old) => {
                  if (!old || !Array.isArray(old)) return old;
                  return old.filter((d) => d.id !== dealId);
                });
                queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats });
                if (DEBUG_REALTIME) {
                  console.log('[Realtime] 🗑️ deals UPDATE with deleted_at — removed from cache', dealId?.slice(0, 8));
                }
                return;
              }

              // Apply update directly em todos os caches (full + per-board).
              // IMPORTANT: Only apply if the incoming status is different from current cache status
              // This prevents Realtime from reverting optimistic updates with stale data
              applyDealPatchToCaches(queryClient, (old) => {
                  if (!old || !Array.isArray(old)) {
                    // #region agent log
                    if (process.env.NODE_ENV !== 'production') {
                      console.log(`[Realtime] ⚠️ Cache is empty or not an array`, { dealId: dealId.slice(0, 8) });
                    }
                    // #endregion
                    return old;
                  }
                  
                  // Find the deal in cache first to check current status
                  const currentDeal = old.find((d) => d.id === dealId);
                  const currentStatus = currentDeal && typeof currentDeal.status === 'string' ? currentDeal.status : null;
                  
                  // #region agent log
                  if (process.env.NODE_ENV !== 'production') {
                    const currentUpdatedAtRaw = currentDeal && (currentDeal.updatedAt || (currentDeal as any).updated_at);
                    const logData = {
                      dealId: dealId.slice(0, 8),
                      dealFound: !!currentDeal,
                      currentStatus: currentStatus?.slice(0, 8) || 'null',
                      currentUpdatedAt: typeof currentUpdatedAtRaw === 'string' ? currentUpdatedAtRaw : 'null',
                      cacheSize: old.length,
                    };
                    console.log(`[Realtime] 🔍 Cache state`, logData);
                  }
                  // #endregion
                  
                  // Deal not found in this cache: skip rather than add.
                  // Per-stage caches are server-filtered by stage — injecting a deal here
                  // causes ghost cards in every stage column when search is active (the bug:
                  // searching "Emily" then any action replicated her card in all columns).
                  // True INSERTs are handled by the INSERT branch above; missed-INSERT from
                  // another tab will sync on the next periodic refetch (staleTime 2 min).
                  if (!currentDeal) {
                    return old;
                  }
                  
                  // Guard: Skip update if incoming status matches current status (no-op)
                  // This prevents Realtime from overwriting newer data with stale payloads
                  if (currentStatus && incomingStatus && currentStatus === incomingStatus) {
                    // #region agent log
                    if (process.env.NODE_ENV !== 'production') {
                      console.log(`[Realtime] ⏭️ Skipping update - status unchanged`, { dealId: dealId.slice(0, 8), status: currentStatus.slice(0, 8) });
                    }
                    // #endregion
                    return old; // No change needed
                  }
                  
                  // Guard: If current status is different from incoming, check if this is a stale update
                  // This prevents Realtime from reverting optimistic updates
                  // CRITICAL: When status differs, we need to be extra careful to avoid stale updates
                  if (currentStatus && incomingStatus && currentStatus !== incomingStatus) {
                    // payloadOldStatus already extracted above
                    
                    // If incoming status matches payload oldStatus, this is stale (reverting to old state)
                    // This happens when we receive a delayed update that reverts our optimistic update
                    if (payloadOldStatus && incomingStatus === payloadOldStatus) {
                      // #region agent log
                      if (process.env.NODE_ENV !== 'production') {
                        const logData = {
                          dealId: dealId.slice(0, 8),
                          currentStatus: currentStatus.slice(0, 8),
                          incomingStatus: incomingStatus.slice(0, 8),
                          payloadOldStatus: payloadOldStatus.slice(0, 8),
                        };
                        console.log(`[Realtime] ⚠️ Skipping update - incoming matches oldStatus (reverting)`, logData);
                      }
                      // #endregion
                      return old; // Skip stale update
                    }
                    
                    // If payload oldStatus is empty, we need to use a heuristic to determine if it's stale
                    // Use updatedAt timestamp to check if the incoming update is newer than current
                    // NOTE: Realtime payload uses snake_case (updated_at), cache uses camelCase (updatedAt)
                    if (!payloadOldStatus || payloadOldStatus === '') {
                      const incomingUpdatedAtRaw = (newData.updated_at || newData.updatedAt) as string | undefined;
                      const incomingUpdatedAt = typeof incomingUpdatedAtRaw === 'string' ? new Date(incomingUpdatedAtRaw).getTime() : null;
                      const currentUpdatedAtRaw = currentDeal && (currentDeal.updatedAt || (currentDeal as any).updated_at);
                      const currentUpdatedAt = typeof currentUpdatedAtRaw === 'string' ? new Date(currentUpdatedAtRaw).getTime() : null;
                      
                      // CRITICAL: When payload.old.status is empty, we can't verify if the update is stale.
                      // Strategy: Trust the server timestamp. If incoming timestamp is newer (even slightly), apply it.
                      // This ensures cross-tab synchronization works even when timestamps are close.
                      // Only skip if incoming timestamp is significantly older (<-100ms), which indicates a stale update.
                      if (incomingUpdatedAt && currentUpdatedAt) {
                        const diffMs = incomingUpdatedAt - currentUpdatedAt;
                        
                        // If incoming timestamp is significantly older (<-100ms), skip it (stale)
                        // This prevents applying updates from previous operations that arrived out of order
                        if (diffMs < -100) {
                          // #region agent log
                          if (process.env.NODE_ENV !== 'production') {
                            const logData = {
                              dealId: dealId.slice(0, 8),
                              currentStatus: currentStatus.slice(0, 8),
                              incomingStatus: incomingStatus.slice(0, 8),
                              currentUpdatedAt: new Date(currentUpdatedAt).toISOString(),
                              incomingUpdatedAt: new Date(incomingUpdatedAt).toISOString(),
                              diffMs: diffMs,
                            };
                            console.log(`[Realtime] ⚠️ Skipping update - incoming timestamp significantly older (stale)`, logData);
                          }
                          // #endregion
                          return old; // Skip stale update
                        }
                        
                        // If incoming timestamp is newer or close (>=-100ms), apply it
                        // This ensures cross-tab synchronization works even when timestamps are close
                        // #region agent log
                        if (process.env.NODE_ENV !== 'production') {
                          const logData = {
                            dealId: dealId.slice(0, 8),
                            currentStatus: currentStatus.slice(0, 8),
                            incomingStatus: incomingStatus.slice(0, 8),
                            currentUpdatedAt: new Date(currentUpdatedAt).toISOString(),
                            incomingUpdatedAt: new Date(incomingUpdatedAt).toISOString(),
                            diffMs: diffMs,
                          };
                          console.log(`[Realtime] ✅ Applying update (empty oldStatus, timestamp newer or close)`, logData);
                        }
                        // #endregion
                        // Continue to apply the update below
                      } else {
                        // Can't compare timestamps, be conservative: only apply if status matches
                        if (incomingStatus === currentStatus) {
                          // #region agent log
                          if (process.env.NODE_ENV !== 'production') {
                            const logData = {
                              dealId: dealId.slice(0, 8),
                              currentStatus: currentStatus.slice(0, 8),
                              incomingStatus: incomingStatus.slice(0, 8),
                              currentUpdatedAt: currentUpdatedAt ? new Date(currentUpdatedAt).toISOString() : 'null',
                              incomingUpdatedAt: incomingUpdatedAt ? new Date(incomingUpdatedAt).toISOString() : 'null',
                            };
                            console.log(`[Realtime] ✅ Applying update (empty oldStatus, can't compare but status matches)`, logData);
                          }
                          // #endregion
                          // Continue to apply the update below
                        } else {
                          // #region agent log
                          if (process.env.NODE_ENV !== 'production') {
                            const logData = {
                              dealId: dealId.slice(0, 8),
                              currentStatus: currentStatus.slice(0, 8),
                              incomingStatus: incomingStatus.slice(0, 8),
                              currentUpdatedAt: currentUpdatedAt ? new Date(currentUpdatedAt).toISOString() : 'null',
                              incomingUpdatedAt: incomingUpdatedAt ? new Date(incomingUpdatedAt).toISOString() : 'null',
                            };
                            console.log(`[Realtime] ⚠️ Skipping update (empty oldStatus, can't compare and status differs)`, logData);
                          }
                          // #endregion
                          return old; // Skip update - too risky without timestamp comparison
                        }
                      }
                    }
                    
                    // If we have both oldStatus and newStatus, and newStatus is different from currentStatus,
                    // this is likely a valid update from another tab - apply it!
                    if (payloadOldStatus) {
                      // #region agent log
                      if (process.env.NODE_ENV !== 'production') {
                        console.log(`[Realtime] ✅ Applying update (has oldStatus, likely from another tab)`, {
                          dealId: dealId.slice(0, 8),
                          currentStatus: currentStatus.slice(0, 8),
                          incomingStatus: incomingStatus.slice(0, 8),
                          payloadOldStatus: payloadOldStatus.slice(0, 8),
                        });
                      }
                      // #endregion
                      // Continue to apply the update below
                    }
                  }
                  
                  // Also apply if currentStatus is null but incomingStatus exists (deal exists but status is missing)
                  if (!currentStatus && incomingStatus) {
                    // #region agent log
                    if (process.env.NODE_ENV !== 'production') {
                      console.log(`[Realtime] ✅ Applying update (currentStatus null but incomingStatus exists)`, {
                        dealId: dealId.slice(0, 8),
                        incomingStatus: incomingStatus.slice(0, 8),
                      });
                    }
                    // #endregion
                    // Continue to apply the update below
                  }
                  
                  const updated = old.map((deal) => {
                    if (deal.id === dealId) {
                      // #region agent log
                      if (process.env.NODE_ENV !== 'production') {
                        const logData = {
                          dealId: dealId.slice(0, 8),
                          oldStatus: typeof deal.status === 'string' ? deal.status.slice(0, 8) : '',
                          newStatus: incomingStatus ? incomingStatus.slice(0, 8) : '',
                        };
                        console.log(`[Realtime] ✅ Applying update to cache`, logData);
                      }
                      // #endregion
                      // Surgical patch: only apply known camelCase fields to the existing deal.
                      // Spreading the full payload (snake_case + camelCase) would add foreign keys
                      // like board_id/contact_id/owner_id that aren't on the clean cached deal,
                      // breaking TQ structural sharing and causing unnecessary re-renders.
                      const patch: Partial<DealView> = {};
                      // Stage (always use stage_id as source of truth)
                      if (typeof incomingStatus === 'string') patch.status = incomingStatus;
                      // Timestamps
                      if (newData.updated_at) patch.updatedAt = newData.updated_at as string;
                      if (newData.last_stage_change_date !== undefined) patch.lastStageChangeDate = (newData.last_stage_change_date as string | null) ?? undefined;
                      if (newData.closed_at !== undefined) patch.closedAt = (newData.closed_at as string | null) ?? undefined;
                      // Win/Loss
                      if (typeof newData.is_won === 'boolean') patch.isWon = newData.is_won;
                      if (typeof newData.is_lost === 'boolean') patch.isLost = newData.is_lost;
                      // Board (in case deal moves between boards)
                      if (typeof newData.board_id === 'string') patch.boardId = newData.board_id;
                      // Tags (bot may update tags without changing stage)
                      if (Array.isArray(newData.tags)) patch.tags = newData.tags as string[];

                      return { ...deal, ...patch };
                    }
                    return deal;
                  });
                  return updated;
                }
              );

              // Fase 1C: se o deal mudou de stage, invalidar stage caches
              // (oldStage e newStage) e ajustar count map. Aplica também
              // pra updates vindos do realtime (outro tab/user moveu).
              if (
                incomingStatus &&
                payloadOldStatus &&
                incomingStatus !== payloadOldStatus &&
                typeof newData.board_id === 'string'
              ) {
                const boardId = newData.board_id as string;
                queryClient.invalidateQueries({
                  queryKey: DEALS_BY_STAGE_KEY(boardId, payloadOldStatus),
                  refetchType: 'active',
                });
                queryClient.invalidateQueries({
                  queryKey: DEALS_BY_STAGE_KEY(boardId, incomingStatus),
                  refetchType: 'active',
                });
                adjustStageCount(queryClient, boardId, payloadOldStatus, -1);
                adjustStageCount(queryClient, boardId, incomingStatus, +1);
              }

              // Invalida mapa contactStageIds usado pelo filtro de etapa no inbox.
              // Sem isso, mover um deal de stage não some do filtro até o staleTime (30s).
              if (incomingStatus && payloadOldStatus && incomingStatus !== payloadOldStatus) {
                queryClient.invalidateQueries({ queryKey: ['allDeal', 'contactStageIds'] });
              }

              // Still invalidate dashboard stats (they need recalculation)
              queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats });
            } else {
              // For other tables or DELETE: debounce invalidation
              if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
              }

              debounceTimerRef.current = setTimeout(() => {
                // Invalidate all pending queries
                pendingInvalidationsRef.current.forEach(queryKey => {
                  // Skip conversations-list invalidation while a deletion is in-progress.
                  if (pendingDeletionIds.size > 0 && queryKey === queryKeys.messagingConversations.all) {
                    return;
                  }
                  if (DEBUG_REALTIME) {
                    console.log(`[Realtime] Invalidating queries (debounced):`, queryKey);
                  }
                  queryClient.invalidateQueries({ queryKey });
                });
                pendingInvalidationsRef.current.clear();
              }, debounceMs);
            }
          }
        }
      );
    });

    // Delay subscription slightly to avoid race condition with previous channel
    // removal in React StrictMode (unmount → remount happens synchronously, but
    // Supabase removeChannel is async on the server side).
    const subscribeTimer = setTimeout(() => {
      channel.subscribe((status) => {
        if (DEBUG_REALTIME) {
          console.log(`[Realtime] Channel ${channelName} status:`, status);
        }

        const wasDisconnected = !isConnectedRef.current;
        isConnectedRef.current = status === 'SUBSCRIBED';

        if (status === 'SUBSCRIBED') {
          console.log(`[Realtime] ✅ Connected to ${tableList.join(', ')}`);
          // If we're reconnecting after an error/timeout, flush stale caches so
          // any events missed during the gap are picked up immediately.
          if (wasDisconnected && tableList.includes('messaging_conversations')) {
            queryClient.invalidateQueries({ queryKey: queryKeys.messagingConversations.all });
          }
        } else if (status === 'CHANNEL_ERROR') {
          console.warn(`[Realtime] Channel error for ${channelName} (will auto-retry)`);
        } else if (status === 'TIMED_OUT') {
          console.warn(`[Realtime] Channel timeout for ${channelName}`);
        } else if (status === 'CLOSED') {
          if (DEBUG_REALTIME) {
            console.warn(`[Realtime] Channel closed for ${channelName}`);
          }
        }
      });
    }, 100);

    channelRef.current = channel;

    // Cleanup (detach-and-forget: null ref immediately, remove async)
    return () => {
      clearTimeout(subscribeTimer);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      const channelToRemove = channelRef.current;
      channelRef.current = null;
      isConnectedRef.current = false;
      if (channelToRemove) {
        sb.removeChannel(channelToRemove);
      }
    };
    // Re-run quando enabled, tables, debounceMs ou organizationId mudarem
    // (orgId entra no filtro server-side e no nome do canal — precisa
    // recriar o canal quando o usuário troca de org / faz login).
  }, [enabled, JSON.stringify(tables), debounceMs, organizationId]);

  return {
    /** Manually trigger a sync */
    sync: () => {
      const tableList = Array.isArray(tables) ? tables : [tables];
      tableList.forEach(table => {
        const keys = getTableQueryKeys(table);
        keys.forEach(queryKey => {
          queryClient.invalidateQueries({ queryKey });
        });
      });
    },
    /** Check if channel is connected */
    isConnected: isConnectedRef.current,
  };
}

/**
 * Subscribe to all CRM-related tables at once
 * Ideal for the main app layout
 */
export function useRealtimeSyncAll(options: UseRealtimeSyncOptions = {}) {
  return useRealtimeSync(['deals', 'contacts', 'activities', 'boards', 'crm_companies'], options);
}

/**
 * Subscribe to Kanban-related tables
 * Optimized for the boards page
 */
export function useRealtimeSyncKanban(options: UseRealtimeSyncOptions = {}) {
  return useRealtimeSync(['deals', 'board_stages'], options);
}

/**
 * Subscribe to Messaging-related tables
 * Optimized for the messaging inbox
 *
 * Também assina `contacts` porque a UI deriva o estado do bot (tag "BOT")
 * de `contacts.ai_paused`. Quando o bot executa stop_bot / start, o
 * backend muda esse campo e precisamos refletir na lista de conversas
 * em tempo real.
 *
 * Em vez de só invalidar (que depende de refetch + latência), patchamos
 * o cache diretamente: percorremos todas as queries ativas de
 * messagingConversations e atualizamos `contactAiPaused` da conversa
 * cujo `contactId` bate com o contato modificado. Padrão idêntico ao
 * que o código já faz para updates de status de mensagens.
 */
export function useRealtimeSyncMessaging(options: UseRealtimeSyncOptions = {}) {
  const queryClient = useQueryClient();
  return useRealtimeSync(
    ['messaging_conversations', 'messaging_messages', 'contacts'],
    {
      ...options,
      onchange: (payload) => {
        if (payload.table === 'contacts' && payload.eventType === 'UPDATE') {
          const row = payload.new as Record<string, unknown> | null;
          const contactId = row?.id as string | undefined;
          if (contactId) {
            const nextAiPaused = (row?.ai_paused as boolean | undefined) ?? false;
            // (1) Patch in-place todas as listas de conversas sem esperar
            //     refetch.
            queryClient.setQueriesData<ConversationView[]>(
              { queryKey: queryKeys.messagingConversations.all },
              (old) => {
                if (!Array.isArray(old)) return old;
                let changed = false;
                const next = old.map((c) => {
                  if (c.contactId !== contactId) return c;
                  if (c.contactAiPaused === nextAiPaused) return c;
                  changed = true;
                  return { ...c, contactAiPaused: nextAiPaused };
                });
                return changed ? next : old;
              },
            );
            // (2) Patch direto no cache de contacts (usado pelo
            //     DealDetailModal via useContacts()). Atualiza aiPaused
            //     no array sem esperar refetch.
            queryClient.setQueriesData<Array<Record<string, unknown>>>(
              { queryKey: queryKeys.contacts.all },
              (old) => {
                if (!Array.isArray(old)) return old;
                let changed = false;
                const next = old.map((c) => {
                  if ((c as { id?: string }).id !== contactId) return c;
                  if ((c as { aiPaused?: boolean }).aiPaused === nextAiPaused) return c;
                  changed = true;
                  return { ...c, aiPaused: nextAiPaused };
                });
                return changed ? next : old;
              },
            );
          }
        }
        options.onchange?.(payload);
      },
    },
  );
}
